/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { type TSlide } from "@/zapi-frontend/stores/presentation";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { SlideOutlineEditor } from "./SlideOutlineEditor";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";

type TSlideCardProps = {
  slide: TSlide;
  index: number;
  onUpdate: (slideId: string, updates: Partial<TSlide>) => void;
  onDelete: (slideId: string) => void;
  selectedIndex: number;
  onSelect: (index: number) => void;
};

export function SlideCard(props: TSlideCardProps) {
  const { t } = useTranslation("projectHome");
  const { slide, onUpdate, index, selectedIndex, onSelect } = props;
  const { isLabs = false, role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);
  const isSelected = selectedIndex === index;

  const [title, setTitle] = useState(slide.title || "");
  const [outline, setOutline] = useState(slide.outline || "");
  const titleRef = useRef<HTMLTextAreaElement>(null);

  const adjustTitleHeight = useCallback(() => {
    const textarea = titleRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, isLabs ? 120 : 150)}px`;
  }, [isLabs]);

  useEffect(() => {
    if (slide.title !== title) setTitle(slide.title || "");
    if (slide.outline !== outline) setOutline(slide.outline || "");
  }, [slide.title, slide.outline]);

  useEffect(() => {
    adjustTitleHeight();
  }, [title, adjustTitleHeight]);

  useEffect(() => {
    const textarea = titleRef.current;
    if (!textarea) return;

    const resizeObserver = new ResizeObserver(() => {
      adjustTitleHeight();
    });

    if (textarea.parentElement) resizeObserver.observe(textarea.parentElement);

    return () => resizeObserver.disconnect();
  }, [adjustTitleHeight]);

  function changeTitleHandler(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const { value } = e.target;
    setTitle(value);
    onUpdate(slide.id, { title: value });
  }

  const changeOutlineHandler = useCallback(
    (value: string) => {
      setOutline(value);
      onUpdate(slide.id, { outline: value });
    },
    [slide.id, onUpdate]
  );

  return (
    <div
      onClick={() => onSelect(index)}
      id={`slide-${slide.id}`}
      className={cn(
        "w-full p-8 flex flex-col gap-4 relative group bg-white shadow-[0px_2px_5px_0px_rgba(24,24,27,0.02),0px_8px_8px_0px_rgba(24,24,27,0.01),0px_18px_11px_0px_rgba(24,24,27,0.01)]",
        isLabs ? "h-[480px]" : "h-[800px]"
      )}
    >
      <FigmaTypography variant="label-2" className={cn(isSelected ? "text-innoveraOrange-400" : "text-innoveraZinc-500")}>
        {t("presentation.slideNumber", { number: index + 1 })}
      </FigmaTypography>

      <div
        className={cn(
          "rounded-lg px-4 pt-3 pb-1 transition-all",
          isSelected ? "border-[3px] border-innoveraZinc-150" : "border border-innoveraZinc-150"
        )}
      >
        <textarea
          ref={titleRef}
          className={cn(
            "font-bold w-full border-none outline-none bg-transparent placeholder:text-innoveraZinc-400 text-innoveraZinc-900 transition-all resize-none overflow-hidden leading-normal",
            "text-[28px] tracking-[-0.28px]"
          )}
          value={title}
          onChange={changeTitleHandler}
          placeholder={t("presentation.slideTitlePlaceholder")}
          rows={1}
          readOnly={!hasEdit || !isSelected}
        />
      </div>

      <div
        className={cn(
          "flex-1 w-full overflow-hidden rounded-lg transition-all",
          isSelected ? "border-[3px] border-innoveraZinc-150" : "border border-innoveraZinc-150"
        )}
      >
        <SlideOutlineEditor
          value={outline}
          onChange={changeOutlineHandler}
          placeholder={t("presentation.keyPointsPlaceholder")}
          isLabs={isLabs}
          isReadOnly={!hasEdit}
          isSelected={isSelected}
        />
      </div>
    </div>
  );
}
